import $ from 'jquery';
import Swiper from 'swiper';
import {Navigation} from 'swiper/modules';

$('.reels').each((i, elem) => {
    const $root = $(elem);
    const $slider = $root.find('.reels__slider');
    const next = $root.find('.reels__nav .swiper-arrow--next')[0];
    const prev = $root.find('.reels__nav .swiper-arrow--prev')[0];

    // With the frame, the xl gap is reduced by 2x border width (140 - 2*30) so
    // the inner borders grow into the gap; the -30px slider margins (CSS) take
    // care of the two outer borders. See _reels.scss.
    const framed = $root.hasClass('reels--framed');

    const swiper = new Swiper($slider[0], {
        modules: [Navigation],
        slidesPerView: 1,
        spaceBetween: 20,
        navigation: {
            nextEl: next,
            prevEl: prev,
        },
        breakpoints: {
            768: {
                slidesPerView: 2,
                spaceBetween: 30,
            },
            992: {
                slidesPerView: 3,
                spaceBetween: 30,
            },
            1200: {
                slidesPerView: 3,
                spaceBetween: framed ? 80 : 140,
            },
        },
    });

    // There are no native controls: play/pause happen by clicking the poster,
    // the play button, or the video itself.
    //
    // Playback follows the *visible position*, not the video: we remember the
    // offset(s) from the first visible slide that are playing, so on navigation
    // whatever slides into that position plays and the previous one stops.
    //
    // The <video> play/pause events are the source of truth (keeps our state and
    // the DOM in sync); `programmaticPause` marks the pauses we trigger in stop()
    // so they don't untrack the position.
    const playingOffsets = new Set();
    const programmaticPause = new WeakSet();

    const wrapOf = slide => slide && slide.querySelector('.reels__video-wrap');
    const videoOf = slide => { const w = wrapOf(slide); return w && w.querySelector('.reels__video'); };
    const indexOf = slide => (slide ? swiper.slides.indexOf(slide) : -1);

    // Start or resume. `restart` rewinds first — used when a *different* video
    // slides into a playing position, so it begins from the top.
    const play = (slide, restart = false) => {
        const wrap = wrapOf(slide);
        const video = wrap && wrap.querySelector('.reels__video');
        if (!video) {
            return;
        }
        // Reveal synchronously so a freshly-entering video doesn't flash its poster.
        wrap.classList.add('reels__video-wrap--started', 'reels__video-wrap--playing');
        if (restart) {
            video.currentTime = 0;
        }
        if (video.paused) {
            video.play()?.catch(() => {});
        }
    };

    // Full reset: back to the poster.
    const stop = slide => {
        const wrap = wrapOf(slide);
        if (!wrap) {
            return;
        }
        wrap.classList.remove('reels__video-wrap--started', 'reels__video-wrap--playing');
        const video = wrap.querySelector('.reels__video');
        if (video) {
            if (!video.paused) {
                programmaticPause.add(video);
                video.pause();
            }
            video.currentTime = 0;
        }
    };

    // Reconcile every slide against the tracked offsets after a navigation.
    const sync = () => {
        const active = swiper.activeIndex;
        const targets = new Set(
            [...playingOffsets]
                .map(offset => active + offset)
                .filter(index => index >= 0 && index < swiper.slides.length)
        );
        swiper.slides.forEach((slide, index) => {
            if (targets.has(index)) {
                play(slide, true);
            } else {
                stop(slide);
            }
        });
    };

    swiper.on('slideChange', sync);

    // Poster / play button -> start or resume.
    $root.on('click', '.reels__poster, .reels__play', ev => {
        ev.preventDefault();
        play(ev.currentTarget.closest('.reels__item'));
    });

    // Click on the video toggles play / pause.
    $root.on('click', '.reels__video', ev => {
        const slide = ev.currentTarget.closest('.reels__item');
        const video = videoOf(slide);
        if (!video) {
            return;
        }
        if (video.paused) {
            play(slide);
        } else {
            video.pause();
        }
    });

    // The video's own state drives tracking + visuals. play/pause don't bubble,
    // so bind directly (slides are static in the DOM).
    $root.find('.reels__video').each((_, video) => {
        const slide = video.closest('.reels__item');

        video.addEventListener('play', () => {
            const wrap = wrapOf(slide);
            if (wrap) {
                wrap.classList.add('reels__video-wrap--started', 'reels__video-wrap--playing');
            }
            const index = indexOf(slide);
            if (index >= 0) {
                playingOffsets.add(index - swiper.activeIndex);
            }
        });

        video.addEventListener('pause', () => {
            // Our own stop() pauses: leave the position tracked, classes handled there.
            if (programmaticPause.has(video)) {
                programmaticPause.delete(video);
                return;
            }
            // User pause: show the play button again (poster stays hidden) and untrack.
            wrapOf(slide)?.classList.remove('reels__video-wrap--playing');
            const index = indexOf(slide);
            if (index >= 0) {
                playingOffsets.delete(index - swiper.activeIndex);
            }
        });

        video.addEventListener('ended', () => {
            const index = indexOf(slide);
            if (index >= 0) {
                playingOffsets.delete(index - swiper.activeIndex);
            }
            stop(slide);
        });
    });
});
